import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  ArrowLeft, FileText, Plus, Trash2, Users, Calendar, Book, X, AlertCircle,
} from 'lucide-react';

interface Exam {
  id: string;
  class_display: string;
  class_normalized?: string;
  subject_code: string | null;
  kind: string;
  title: string;
  description: string | null;
  study_content: string | null;
  material_url: string | null;
  scheduled_at: string;
  duration_minutes: number | null;
  status: string;
  teacher_name?: string;
  created_at?: string;
}

interface TeacherClass {
  id: string;
  class_normalized: string;
  class_display: string;
  subject_code: string | null;
  special_role: string | null;
}

interface Subject { code: string; display_name: string | null; }

const KIND_LABELS: Record<string, string> = {
  schulaufgabe: 'Schulaufgabe',
  stegreifaufgabe: 'Stegreifaufgabe',
  ex: 'Ex',
  kurzarbeit: 'Kurzarbeit',
  test: 'Test',
  termin: 'Termin',
  projekt: 'Projekt',
  referat: 'Referat',
  sonstiges: 'Sonstiges',
};

const STATUS_LABELS: Record<string, string> = {
  'angekündigt': 'angekündigt',
  'in_korrektur': 'in Korrektur',
  'korrigiert': 'korrigiert',
  'zurückgegeben': 'zurückgegeben',
};

const STATUS_COLORS: Record<string, string> = {
  'angekündigt': 'bg-yellow-500/20 text-yellow-200 border-yellow-500/40',
  'in_korrektur': 'bg-blue-500/20 text-blue-200 border-blue-500/40',
  'korrigiert': 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
  'zurückgegeben': 'bg-gray-500/20 text-gray-300 border-gray-500/40',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const ArbeitenPage: React.FC = () => {
  const { user } = useAuthStore();
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';

  const [exams, setExams] = useState<Exam[]>([]);
  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [showClassForm, setShowClassForm] = useState(false);
  const [newClass, setNewClass] = useState({ class: '', subject_code: '', special_role: '' });

  const [showExamForm, setShowExamForm] = useState(false);
  const [newExam, setNewExam] = useState({
    class: '', subject_code: '', kind: 'schulaufgabe',
    title: '', description: '', study_content: '', material_url: '',
    scheduled_at: '', duration_minutes: '',
  });

  const load = useCallback(async () => {
    try {
      const [examsR, subjectsR] = await Promise.all([
        api<{ exams: Exam[]; role: string }>('/api/teacher/exams'),
        api<{ subjects: Subject[] }>('/api/subjects').catch(() => ({ subjects: [] as Subject[] })),
      ]);
      setExams(examsR.exams);
      setSubjects(subjectsR.subjects);
      if (isTeacher) {
        const classesR = await api<{ classes: TeacherClass[] }>('/api/teacher/classes');
        setClasses(classesR.classes);
      }
    } catch (err: any) {
      setError(err?.message || 'Fehler beim Laden');
    }
  }, [isTeacher]);

  useEffect(() => { load(); }, [load]);

  const createClass = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClass.class.trim()) return;
    setBusy('class'); setError(null);
    try {
      await api('/api/teacher/classes', {
        method: 'POST',
        body: JSON.stringify({
          class: newClass.class,
          subject_code: newClass.subject_code || null,
          special_role: newClass.special_role || null,
        }),
      });
      setNewClass({ class: '', subject_code: '', special_role: '' });
      setShowClassForm(false);
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const deleteClass = async (id: string) => {
    if (!window.confirm('Zuordnung löschen?')) return;
    setBusy(id);
    try {
      await api(`/api/teacher/classes/${id}`, { method: 'DELETE' });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const createExam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newExam.title.trim() || !newExam.class.trim() || !newExam.scheduled_at) return;
    setBusy('exam'); setError(null);
    try {
      await api('/api/teacher/exams', {
        method: 'POST',
        body: JSON.stringify({
          class: newExam.class,
          subject_code: newExam.subject_code || null,
          kind: newExam.kind,
          title: newExam.title,
          description: newExam.description || null,
          study_content: newExam.study_content || null,
          material_url: newExam.material_url || null,
          scheduled_at: newExam.scheduled_at,
          duration_minutes: newExam.duration_minutes || null,
        }),
      });
      setNewExam({ class: '', subject_code: '', kind: 'schulaufgabe', title: '', description: '', study_content: '', material_url: '', scheduled_at: '', duration_minutes: '' });
      setShowExamForm(false);
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const deleteExam = async (id: string) => {
    if (!window.confirm('Prüfung wirklich löschen?')) return;
    setBusy(id);
    try {
      await api(`/api/teacher/exams/${id}`, { method: 'DELETE' });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const updateStatus = async (id: string, status: string) => {
    setBusy(id);
    try {
      await api(`/api/teacher/exams/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const classOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { display: string; norm: string }[] = [];
    for (const c of classes) {
      if (!seen.has(c.class_normalized)) {
        seen.add(c.class_normalized);
        opts.push({ display: c.class_display, norm: c.class_normalized });
      }
    }
    return opts;
  }, [classes]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <FileText className="w-6 h-6 text-orange-400" />
        <h1 className="text-xl font-semibold">Prüfungen & Arbeiten</h1>
        {isTeacher && <span className="ml-auto text-xs bg-orange-500/20 text-orange-200 px-2 py-1 rounded border border-orange-500/40">Lehrkraft-Ansicht</span>}
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Teacher: class assignments */}
        {isTeacher && (
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-orange-400" /> Meine Klassen & Fächer</h2>
              <button onClick={() => setShowClassForm(v => !v)}
                className="text-sm bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded flex items-center gap-1">
                {showClassForm ? <><X className="w-3 h-3" /> Schließen</> : <><Plus className="w-3 h-3" /> Klasse übernehmen</>}
              </button>
            </div>

            {showClassForm && (
              <form onSubmit={createClass} className="bg-gray-900/60 border border-gray-700 rounded p-3 mb-3 space-y-2">
                <div className="flex flex-wrap gap-2">
                  <input type="text" value={newClass.class} onChange={e => setNewClass({ ...newClass, class: e.target.value })}
                    placeholder="z.B. 7G" required
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm" />
                  <select value={newClass.subject_code} onChange={e => setNewClass({ ...newClass, subject_code: e.target.value })}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm">
                    <option value="">(Fach – optional)</option>
                    {subjects.map(s => <option key={s.code} value={s.code}>{s.display_name ?? s.code}</option>)}
                  </select>
                  <select value={newClass.special_role} onChange={e => setNewClass({ ...newClass, special_role: e.target.value })}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm">
                    <option value="">(Sonderaufgabe – optional)</option>
                    <option value="Klassenleitung">Klassenleitung</option>
                    <option value="Stellvertretung">Stellv. Klassenleitung</option>
                    <option value="Tutor">Tutor/in</option>
                    <option value="Beratungslehrer">Beratungslehrer/in</option>
                  </select>
                  <button type="submit" disabled={busy === 'class' || !newClass.class.trim()}
                    className="ml-auto bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 px-4 py-2 rounded text-sm font-medium flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Hinzufügen
                  </button>
                </div>
                <p className="text-xs text-gray-500">Du kannst pro Klasse mehrere Fächer + eine Klassenleitungs-Rolle anlegen.</p>
              </form>
            )}

            {classes.length === 0 ? (
              <p className="text-gray-500 text-sm">Noch keine Klassen zugeordnet. Nach der ersten Zuordnung kannst du Prüfungen für diese Klasse einstellen.</p>
            ) : (
              <ul className="space-y-1">
                {classes.map(c => (
                  <li key={c.id} className="flex items-center gap-2 py-1">
                    <span className="font-mono bg-gray-700 px-2 py-0.5 rounded text-sm">{c.class_display}</span>
                    {c.subject_code && <span className="text-sm text-gray-300"><Book className="inline w-3 h-3 mr-1" />{c.subject_code}</span>}
                    {c.special_role && <span className="text-xs bg-orange-500/20 text-orange-200 px-2 py-0.5 rounded">{c.special_role}</span>}
                    <button onClick={() => deleteClass(c.id)} disabled={busy === c.id}
                      className="ml-auto p-1 rounded hover:bg-red-600/40 text-gray-400 hover:text-red-300" title="Entfernen">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Teacher: post new exam */}
        {isTeacher && (
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-orange-400" /> Prüfung / Arbeit einstellen</h2>
              <button onClick={() => setShowExamForm(v => !v)}
                className="text-sm bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded flex items-center gap-1">
                {showExamForm ? <><X className="w-3 h-3" /> Schließen</> : <><Plus className="w-3 h-3" /> Neue Prüfung</>}
              </button>
            </div>

            {showExamForm && (
              <form onSubmit={createExam} className="bg-gray-900/60 border border-gray-700 rounded p-3 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <select required value={newExam.class} onChange={e => setNewExam({ ...newExam, class: e.target.value })}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm">
                    <option value="">Klasse…</option>
                    {classOptions.map(o => <option key={o.norm} value={o.display}>{o.display}</option>)}
                  </select>
                  <select value={newExam.subject_code} onChange={e => setNewExam({ ...newExam, subject_code: e.target.value })}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm">
                    <option value="">Fach…</option>
                    {subjects.map(s => <option key={s.code} value={s.code}>{s.display_name ?? s.code}</option>)}
                  </select>
                  <select value={newExam.kind} onChange={e => setNewExam({ ...newExam, kind: e.target.value })}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm">
                    {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <input type="datetime-local" required value={newExam.scheduled_at}
                    onChange={e => setNewExam({ ...newExam, scheduled_at: e.target.value })}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm" />
                  <input type="number" min="1" value={newExam.duration_minutes}
                    onChange={e => setNewExam({ ...newExam, duration_minutes: e.target.value })}
                    placeholder="Dauer (min)"
                    className="w-32 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm" />
                </div>
                <input type="text" required value={newExam.title}
                  onChange={e => setNewExam({ ...newExam, title: e.target.value })}
                  placeholder="Titel, z.B. '1. Schulaufgabe Deutsch'"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm" />
                <textarea value={newExam.study_content}
                  onChange={e => setNewExam({ ...newExam, study_content: e.target.value })}
                  placeholder="Lerninhalte (Was kommt dran?)"
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm" />
                <textarea value={newExam.description}
                  onChange={e => setNewExam({ ...newExam, description: e.target.value })}
                  placeholder="Beschreibung / Hinweise (optional)"
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm" />
                <input type="url" value={newExam.material_url}
                  onChange={e => setNewExam({ ...newExam, material_url: e.target.value })}
                  placeholder="Zusatzmaterial-Link (optional)"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm" />
                <button type="submit" disabled={busy === 'exam'}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 px-4 py-2 rounded text-sm font-semibold flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Einstellen & Klasse benachrichtigen
                </button>
              </form>
            )}
          </section>
        )}

        {/* Exam list — both roles */}
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h2 className="font-semibold flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-red-400" />
            {isTeacher ? 'Meine Prüfungen' : 'Anstehende Prüfungen & Arbeiten'}
          </h2>

          {exams.length === 0 ? (
            <p className="text-gray-500 text-sm">
              {isTeacher
                ? 'Noch keine Prüfungen eingestellt. Nutze den Button oben.'
                : 'Keine Prüfungen für deine Klasse angekündigt.'}
            </p>
          ) : (
            <ul className="space-y-3">
              {exams.map(ex => (
                <li key={ex.id} className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{ex.title}</span>
                        <span className="text-xs bg-orange-500/20 text-orange-200 px-1.5 py-0.5 rounded">
                          {KIND_LABELS[ex.kind] ?? ex.kind}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded border ${STATUS_COLORS[ex.status] ?? 'bg-gray-700 text-gray-300'}`}>
                          {STATUS_LABELS[ex.status] ?? ex.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-400">
                        <span>🗓 {formatDateTime(ex.scheduled_at)}</span>
                        <span className="bg-gray-700 px-1.5 py-0.5 rounded">Klasse {ex.class_display}</span>
                        {ex.subject_code && <span className="bg-gray-700 px-1.5 py-0.5 rounded">{ex.subject_code}</span>}
                        {ex.duration_minutes && <span>⏱ {ex.duration_minutes}min</span>}
                        {ex.teacher_name && <span>👩‍🏫 {ex.teacher_name}</span>}
                      </div>
                      {ex.description && <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{ex.description}</p>}
                      {ex.study_content && (
                        <div className="mt-2 p-2 rounded bg-gray-900 border border-gray-700 text-sm">
                          <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Lerninhalte</div>
                          <div className="whitespace-pre-wrap text-gray-200">{ex.study_content}</div>
                        </div>
                      )}
                      {ex.material_url && (
                        <a href={ex.material_url} target="_blank" rel="noreferrer"
                          className="inline-block mt-2 text-sm text-blue-400 hover:text-blue-300">
                          📎 Zusatzmaterial
                        </a>
                      )}
                    </div>
                    {isTeacher && (
                      <div className="flex flex-col gap-1 items-end">
                        <select value={ex.status} onChange={e => updateStatus(ex.id, e.target.value)}
                          disabled={busy === ex.id}
                          className="text-xs px-2 py-1 bg-gray-700 border border-gray-600 rounded">
                          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <button onClick={() => deleteExam(ex.id)} disabled={busy === ex.id}
                          className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Löschen
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
};

export default ArbeitenPage;
